import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box } from '@99designs/emu';
import { Button, Heading } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces, typography } from '@99designs/emu/settings';
import { IconDownload1 } from '@99designs/icons';
import { ReactComponent as ShareIcon } from '../../../../public/images/design-without-borders/icons/Share.svg';
import { trackDownload } from '../../../lib/events';
import { Logo } from './Logo';
import Share from './Share/Share';
import { DWB21_Cream } from './colors';

const Wrapper = styled(Box)`
    top: 0;
    height: 80px;
    position: sticky;
    background-color: ${DWB21_Cream};
    display: flex;
    z-index: 300;
    justify-content: space-between;
    padding: ${spaces['1bu']} 40px;
    align-items: center;

    ${mediaQuery.between('small', 'medium')} {
        padding: ${spaces['0.75bu']} ${spaces['1bu']};
        height: 60px;
    }
`;

const StyledMobileButtons = styled(Box)`
    display: flex;
    justify-content: center;
    align-items: center;
    ${mediaQuery.minimum('medium')} {
        display: none;
    }
`;

const NavActionGroup = styled(Box)`
    display: flex;
    gap: ${spaces['1bu']};
    ${mediaQuery.between('small', 'medium')} {
        gap: ${spaces['0.5bu']};
    }
`;

const StyledDesktopLogo = styled(Box)`
    display: flex;
    flex: 1;
    ${mediaQuery.only('medium')} {
        flex: unset;
    }
`;

const StyledDesktopTitle = styled(Box)`
    display: flex;
    flex: 1;
    justify-content: center;
    @media screen and (max-width: 1100px) {
        display: none;
    }
`;

const StyledDesktopButtons = styled(Box)`
    display: flex;
    flex: 1;
    justify-content: flex-end;
    ${mediaQuery.only('small')} {
        display: none;
    }
`;

const Title = styled(Heading)`
    font-family: ${typography.fontFamilies.attribution};
    font-style: ${typography.fontStyles.em};
    font-size: ${typography.typeScale.size3}px;
    letter-spacing: 0px;
`;

const dwbReportName = 'Designer_Survey_Result_2022.pdf';
const designerSurveyReportUrl = `/design-without-borders/${dwbReportName}`;

interface Props {
    setNavHeight: (state: number) => void;
}

export default function Nav({ setNavHeight }: Props) {
    const [showModal, showSocialShareMenu] = useState(false);
    const currentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (currentRef && currentRef.current) {
            setNavHeight(currentRef.current?.offsetHeight);
        }
    }, [currentRef.current?.offsetHeight, setNavHeight]);

    return (
        <Wrapper ref={currentRef}>
            <StyledDesktopLogo>
                <Logo />
            </StyledDesktopLogo>

            <StyledDesktopTitle>
                <Title accessibilityLevel={7}>Design Without Borders 2022</Title>
            </StyledDesktopTitle>

            <StyledMobileButtons>
                <NavActionGroup>
                    <Button
                        href={designerSurveyReportUrl}
                        openInNewTab={true}
                        size="small"
                        appearance="primary"
                        onClick={() =>
                            trackDownload({
                                location: '21DWBHeader',
                                file_name: dwbReportName,
                                file_type: 'pdf',
                            })
                        }
                    >
                        View full report
                    </Button>
                    <Button
                        size="small"
                        onClick={() => showSocialShareMenu(true)}
                        appearance="tertiary"
                    >
                        Share
                    </Button>
                </NavActionGroup>
            </StyledMobileButtons>
            <StyledDesktopButtons>
                <NavActionGroup>
                    <Button
                        href={designerSurveyReportUrl}
                        openInNewTab={true}
                        appearance="primary"
                        data-testid="download-report-desktop"
                        onClick={() =>
                            trackDownload({
                                location: '21DWBHeader',
                                file_name: dwbReportName,
                                file_type: 'pdf',
                            })
                        }
                    >
                        <IconDownload1 />
                        &nbsp; View full report
                    </Button>
                    <Button
                        onClick={() => showSocialShareMenu(true)}
                        appearance="tertiary"
                        data-testid="share-desktop"
                    >
                        <ShareIcon />
                        &nbsp; Share
                    </Button>
                </NavActionGroup>
            </StyledDesktopButtons>
            {showModal ? <Share showSocialShareMenu={showSocialShareMenu} /> : null}
        </Wrapper>
    );
}
